import { BLOCK_SECONDS, parseChainAmount } from '../core/pricing'
import type { Coin } from '../core/types'
import type { DomainLease, EscrowAccountDto } from './types'

/*
  The one place deposit money is computed.

  Billing, Overview, the Instances list and the instance page all describe the
  same deposits, and they used to read their runway from an escrow field no
  chain shape carries (`estimatedBlocksRemaining`), so every one of them said
  "Unknown" while anything ran. The rate is the lease price; the deposit left
  is the escrow balance less what has accrued since it was last settled.

  pkg.akt.dev/node@v1.1.1 (pinned by `node/go.mod`), x/escrow/keeper/keeper.go:
  `accountSettle` sets `SettledAt = ctx.BlockHeight()` (l.463-464), and
  `accountSettleFullBlocks` deducts `rate × (height − SettledAt)` from the funds
  (l.1080-1116). Funds otherwise only grow, on deposit (l.442). So between two
  settlements the balance is stale by exactly the unsettled blocks, and the
  estimate below is that arithmetic, done early.
*/

export const BLOCKS_PER_DAY = (24 * 60 * 60) / BLOCK_SECONDS
/** The low-runway threshold: Overview's tile, its attention list, and Billing all flag at this. */
export const LOW_RUNWAY_BLOCKS = BLOCKS_PER_DAY * 3

export type BillingAccountState = 'active' | 'insufficient-funds' | 'closed' | 'unknown'

export interface DepositFigures {
  dseq: string
  state: BillingAccountState
  /** Per block: the sum of this dseq's active lease prices. Null if any is unreadable, or none is active. */
  rate: Coin | null
  /** The deposit now: estimated from settlement heights when it can be, else the raw balance. */
  left: Coin | null
  /** True when `left` was derived from settlement heights rather than read. */
  estimated: boolean
  /** Balance plus transferred: what was deposited in all. Same denomination only. */
  total: Coin | null
  /** Whole blocks `left` pays for at `rate`. Null without both. */
  runwayBlocks: number | null
}

function usableCoin(coin: Coin | null | undefined): Coin | null {
  if (!coin?.denom || !coin.amount || !/^-?\d+$/.test(coin.amount)) return null
  return coin
}

export function normalizedState(state: unknown): BillingAccountState {
  // Typed as `unknown` on purpose. This value crosses the network, and a chain
  // whose escrow shape moves the state one level down sends an object here;
  // calling a string method on it took the whole billing screen down. An
  // unreadable state is "unknown", which the page already renders honestly.
  if (typeof state !== 'string') return 'unknown'
  switch (state.toLowerCase()) {
    case 'open':
    case 'active':
      return 'active'
    case 'insufficient_funds':
    case 'insufficient-funds':
    // escrow/v1's name for it: the settlement that ran the funds below zero.
    case 'overdrawn':
      return 'insufficient-funds'
    case 'closed':
      return 'closed'
    default:
      return 'unknown'
  }
}

/**
 * A lease price as an integer coin, or null.
 *
 * Lease prices arrive as chain decimals (`"25.000000000000000000"`), which the
 * billing sum rejects. `parseChainAmount` accepts exactly the decimals that
 * denote a whole base unit, so nothing is rounded on the way to the sum.
 */
export function integerRate(coin: Coin | null | undefined): Coin | null {
  const amount = parseChainAmount(coin?.amount)
  return coin?.denom && amount !== null ? { denom: coin.denom, amount: amount.toString() } : null
}

export function sumCoins(coins: Array<Coin | null>): Coin | null {
  const usable = coins.map(usableCoin)
  if (usable.some((coin) => coin === null) || usable.length === 0) return null
  const denom = usable[0]!.denom
  if (usable.some((coin) => coin!.denom !== denom)) return null
  try {
    return { denom, amount: usable.reduce((total, coin) => total + BigInt(coin!.amount), 0n).toString() }
  } catch {
    return null
  }
}

/** The deployment an escrow account pays for: its dseq, or the second part of its xid. */
export function escrowDseq(escrow: EscrowAccountDto): string {
  if (escrow.dseq) return escrow.dseq
  const parts = typeof escrow.xid === 'string' ? escrow.xid.split('/') : []
  return parts.length >= 2 ? parts[1]! : ''
}

/** A block height from the wire: int64 as a string or a number; "0" and anything else unreadable is null. */
export function parseHeight(value: unknown): number | null {
  const text = typeof value === 'number' ? String(value) : value
  if (typeof text !== 'string' || !/^\d+$/.test(text)) return null
  const number = Number(text)
  return Number.isSafeInteger(number) && number > 0 ? number : null
}

/**
 * The deposit now: `balance` less what `rate` (per block) has accrued since
 * the account last settled at `settledAt`, floored at zero, and marked
 * estimated. When that cannot be computed (no rate, a zero rate, another
 * denomination, or a height missing) it is the balance as read, not estimated.
 *
 * This is the one implementation: Billing, Overview and Instances reach it
 * through `depositFigures`, the instance page's Costs tab through `buildMoney`.
 */
export function estimateDepositLeft(balance: Coin | null | undefined, rate: Coin | null | undefined, settledAt: unknown, chainHeight: unknown): { left: Coin | null; estimated: boolean } {
  if (!balance?.denom || !/^-?\d+$/.test(balance.amount ?? '')) return { left: null, estimated: false }
  // escrow/v1 lets an overdrawn account's funds go below zero. That is the
  // chain's shortfall, not money the tenant has: nothing is left, which is a
  // known zero rather than an unreadable balance.
  if (balance.amount.startsWith('-')) return { left: { denom: balance.denom, amount: '0' }, estimated: false }
  const perBlock = rate?.denom === balance.denom && /^\d+$/.test(rate.amount ?? '') ? BigInt(rate.amount) : 0n
  const from = parseHeight(settledAt)
  const now = parseHeight(chainHeight)
  if (perBlock <= 0n || from === null || now === null) return { left: balance, estimated: false }
  const remaining = BigInt(balance.amount) - perBlock * BigInt(Math.max(0, now - from))
  return { left: { denom: balance.denom, amount: (remaining > 0n ? remaining : 0n).toString() }, estimated: true }
}

/**
 * One deposit's figures from its escrow account, the tenant's leases and the
 * chain height. Nothing here guesses: whatever is unreadable is null.
 */
export function depositFigures(escrow: EscrowAccountDto, leases: readonly DomainLease[], chainHeight: number | null): DepositFigures {
  const dseq = escrowDseq(escrow)
  const state = normalizedState(escrow.state)
  const balance = usableCoin(escrow.balance)
  const total = balance ? sumCoins([balance, usableCoin(escrow.transferred)]) : null

  const active = state === 'active' ? leases.filter((lease) => lease.dseq === dseq && lease.state === 'active') : []
  // A missing price on any active lease makes the rate unavailable: a partial
  // sum would be a confident number that is too low.
  const rate = active.length > 0 ? sumCoins(active.map((lease) => integerRate(lease.rate))) : null
  const usableRate = rate && balance && rate.denom === balance.denom && BigInt(rate.amount) > 0n ? rate : null

  const { left, estimated } = estimateDepositLeft(balance, usableRate, escrow.settledAt, chainHeight)

  const runwayBlocks = usableRate && left && BigInt(left.amount) >= 0n
    ? Number(BigInt(left.amount) / BigInt(usableRate.amount))
    : null

  return { dseq, state, rate, left, estimated, total, runwayBlocks }
}

/** Whole minutes `blocks` last at the chain's block time. Floored: never overstate a runway. */
export function runwayMinutes(blocks: number): number {
  return Math.floor((blocks * BLOCK_SECONDS) / 60)
}
